import React, { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { api, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileDropzone } from "@/components/FileTools";
import { X } from "lucide-react";
import { t } from "@/i18n";
import { useApp } from "@/context/AppContext";
import { PROPERTY_TYPES } from "@/lib/propertyTypes";

const EDIT_FIELDS = {
  title: "", description: "", location: "", city: "", country: "",
  price: "", bedrooms: "", bathrooms: "", area_sqm: "",
  commission_pct: "3", property_type: "villa",
};

const toEditForm = (p) => ({
  title: p.title ?? "",
  description: p.description ?? "",
  location: p.location ?? "",
  city: p.city ?? "",
  country: p.country ?? "",
  price: p.price ?? "",
  bedrooms: p.bedrooms ?? 0,
  bathrooms: p.bathrooms ?? 0,
  area_sqm: p.area_sqm ?? 0,
  commission_pct: p.commission_pct ?? 3,
  property_type: p.property_type ?? "villa",
});

const inputCls = "bg-white border-[#E5E5E3] text-[#1A1A1A] focus-visible:ring-[#B8965A]";

/**
 * Standalone property edit dialog. Works for owner AND admin (backend
 * `PATCH /properties/{id}` already allows admin to edit any listing).
 */
export default function EditPropertyDialog({ property, open, onClose, onSaved }) {
  const { lang } = useApp();
  const [form, setForm] = useState(EDIT_FIELDS);
  const [saving, setSaving] = useState(false);
  const [live, setLive] = useState(property);

  useEffect(() => {
    setLive(property);
    setForm(property ? toEditForm(property) : EDIT_FIELDS);
  }, [property]);

  const removeImage = useCallback(async (fileId) => {
    if (!live) return;
    try {
      const { data } = await api.delete(`/properties/${live.id}/images/${fileId}`);
      setLive(data);
      onSaved?.(data);
      toast.success("Foto rimossa");
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  }, [live, onSaved]);

  const addImage = useCallback(async ({ file_id }) => {
    if (!live || !file_id) return;
    try {
      const fd = new FormData();
      fd.append("file_id", file_id);
      const { data } = await api.post(`/properties/${live.id}/images`, fd);
      setLive(data);
      onSaved?.(data);
      toast.success("Foto aggiunta");
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  }, [live, onSaved]);

  const save = async () => {
    if (!live) return;
    setSaving(true);
    try {
      const body = {
        title: form.title,
        description: form.description,
        location: form.location,
        city: form.city,
        country: form.country,
        price: parseFloat(form.price) || 0,
        bedrooms: parseInt(form.bedrooms) || 0,
        bathrooms: parseInt(form.bathrooms) || 0,
        area_sqm: parseFloat(form.area_sqm) || 0,
        commission_pct: parseFloat(form.commission_pct) || 3,
        property_type: form.property_type || "villa",
      };
      await api.patch(`/properties/${live.id}`, body);
      const wasRejected = live.status === "rejected";
      toast.success(wasRejected ? "Modifiche salvate — l'inserzione torna in revisione" : "Modifiche salvate");
      onSaved?.({ ...live, ...body });
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="bg-white border-[#E5E5E3] text-[#1A1A1A] max-w-2xl max-h-[92vh] overflow-y-auto"
                     data-testid={`edit-property-dialog-${live?.id || ""}`}>
        <DialogHeader>
          <DialogTitle className="text-2xl font-light" style={{ fontFamily: "Cormorant Garamond, serif" }}>
            Modifica inserzione
          </DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 py-2">
          <div>
            <Label className="text-[#4A4A4A]">Titolo</Label>
            <Input data-testid="edit-title" className={inputCls} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          </div>
          <div>
            <Label className="text-[#4A4A4A]">Descrizione</Label>
            <Textarea data-testid="edit-description" className={inputCls} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-[#4A4A4A]">Città</Label>
              <Input data-testid="edit-city" className={inputCls} value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
            </div>
            <div>
              <Label className="text-[#4A4A4A]">Paese</Label>
              <Input data-testid="edit-country" className={inputCls} value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} />
            </div>
          </div>
          <div>
            <Label className="text-[#4A4A4A]">Indirizzo / zona</Label>
            <Input data-testid="edit-location" className={inputCls} value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div><Label className="text-[#4A4A4A]">Prezzo (€)</Label><Input data-testid="edit-price" className={inputCls} type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} /></div>
            <div><Label className="text-[#4A4A4A]">Camere</Label><Input data-testid="edit-bedrooms" className={inputCls} type="number" value={form.bedrooms} onChange={(e) => setForm({ ...form, bedrooms: e.target.value })} /></div>
            <div><Label className="text-[#4A4A4A]">Bagni</Label><Input data-testid="edit-bathrooms" className={inputCls} type="number" value={form.bathrooms} onChange={(e) => setForm({ ...form, bathrooms: e.target.value })} /></div>
            <div><Label className="text-[#4A4A4A]">Area (m²)</Label><Input data-testid="edit-area" className={inputCls} type="number" value={form.area_sqm} onChange={(e) => setForm({ ...form, area_sqm: e.target.value })} /></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-[#4A4A4A]">Tipo di immobile</Label>
              <Select value={form.property_type} onValueChange={(v) => setForm({ ...form, property_type: v })}>
                <SelectTrigger data-testid="edit-property-type" className={inputCls}>
                  <SelectValue placeholder="Seleziona il tipo…" />
                </SelectTrigger>
                <SelectContent>
                  {PROPERTY_TYPES.map((pt) => (
                    <SelectItem key={pt.value} value={pt.value}>{pt[lang] || pt.it}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[#4A4A4A]">Commissione (%)</Label>
              <Input data-testid="edit-commission" className={inputCls} type="number" step="0.1" value={form.commission_pct} onChange={(e) => setForm({ ...form, commission_pct: e.target.value })} />
            </div>
          </div>

          <div className="border-t border-[#E5E5E3] pt-4 mt-2">
            <Label className="text-[#4A4A4A] mb-3 block">Foto ({live?.image_paths?.length || 0})</Label>
            {live?.image_paths?.length > 0 && (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mb-3" data-testid="edit-photos-grid">
                {live.image_paths.map((fid) => (
                  <div key={fid} className="relative group aspect-square rounded overflow-hidden border border-[#E5E5E3]">
                    <img src={fileUrl(fid)} alt="" className="h-full w-full object-cover" />
                    <button type="button" onClick={() => removeImage(fid)} data-testid={`remove-photo-${fid}`}
                            className="absolute top-1 right-1 h-6 w-6 rounded-full bg-red-600 hover:bg-red-700 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                            aria-label="Rimuovi foto">
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <FileDropzone testId="edit-add-photo" kind="image" accept="image/*" label={t("add_more_photos", lang)} onUploaded={addImage} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="text-[#6B6B6B]">{t("cancel", lang)}</Button>
          <Button onClick={save} disabled={saving || !form.title || !form.city}
                  data-testid="my-listing-edit-save"
                  className="bg-[#B8965A] hover:bg-[#a08248] text-white rounded-full">
            {saving ? "…" : t("save", lang)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
